import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useStore } from "store";
import { useSearchParams } from "react-router-dom";
import { useWindowDimensions } from "resources/BrowserManager";
import ApiManager from "resources/ApiManager/api";

import {
  Typography,
  Space,
  Tag,
  Tooltip,
  Dropdown,
  Button,
  Flex,
  Badge,
  message,
} from "antd";
const { Text } = Typography;
import {
  CrownFilled,
  InfoCircleFilled,
  RedoOutlined,
  DownOutlined,
  CheckOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  ClockCircleOutlined,
  GiftFilled,
} from "@ant-design/icons";

import CONSTANTS from "shared/constants";

import "./style.scss";
const styleWrapperClass = "rfcCard";

import { StatusIcon } from "../../../routes/Certificates/components";
import FeatureTag from "components/Shared/FeatureTag";

import strings from "resources/strings";

export async function getUserRfcs(rfc = null) {
  const apiRes = await ApiManager.getUserRfcs(rfc);

  //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
  if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
    return;
  }

  if (apiRes.response.code === 3000) {
    return apiRes.response.code;
  } else if (apiRes.response.code !== 2000) {
    return -1;
  }

  const { rfcs } = apiRes.data;
  const keyedRfcs = rfcs.reduce(
    //eslint-disable-next-line
    (obj, item) => ((obj[item.rfc] = item), obj),
    {},
  );

  return keyedRfcs;
}

export function PlanTag({ planName, isClickable, cursor }) {
  const navigate = useNavigate();

  if (!!!isClickable) {
    isClickable = false;
  }

  if (!!!cursor) {
    cursor = "default";
  }

  let color, displayName;
  if (planName === "pro") {
    color = "gold";
    displayName = (
      <>
        <CrownFilled />
        Pro
      </>
    );
  } else if (planName === "basic") {
    color = "blue";
    displayName = "Básico";
  } else if (planName === "free") {
    color = "red";
    displayName = "Demo";
  } else {
    color = "gray";
    displayName = "N/A";
  }

  return (
    <Tag
      color={color}
      onClick={() => (isClickable ? navigate("/plan") : null)}
      style={{ cursor: cursor }}
    >
      <Space>{displayName}</Space>
    </Tag>
  );
}

export function RfcCardHorizontal(props) {
  const { width } = useWindowDimensions();

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlHasSubscriptionUpgradeParameters =
    searchParams.get("r") && searchParams.get("p");

  const user = useStore((state) => state.user);
  const activeRfc = useStore((state) => state.activeRfc);
  const setActiveRfc = useStore((state) => state.setActiveRfc);
  const updateUserRfc = useStore((state) => state.updateUserRfc);

  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);

  const active_rfc_obj =
    activeRfc !== "nuevo-rfc"
      ? props.rfc
        ? user.rfcs[props.rfc]
        : user.rfcs[activeRfc]
      : null;

  const smallCard = width <= 1100;

  const getRfcUpdateScheduleInfo = (schedule) => {
    if (schedule?.startsWith("monthly")) {
      const dom = schedule.split("_")[1];
      return `Día ${dom} del mes`;
    }

    switch (schedule) {
      case "daily":
        return "Diario";
      case "weekly_sun":
        return "Domingo";
      case "weekly_mon":
        return "Lunes";
      case "weekly_tue":
        return "Martes";
      case "weekly_wed":
        return "Miércoles";
      case "weekly_thu":
        return "Jueves";
      case "weekly_fri":
        return "Viernes";
      case "weekly_sat":
        return "Sábado";
      case "manual":
        return "Manual";
      default:
        return null;
    }
  };

  const updateRfc = async () => {
    setIsLoading(true);
    const rfcRes = await getUserRfcs(active_rfc_obj.rfc);
    if (rfcRes === -1) {
      setIsLoading(false);
      message.error(strings.alertUnexpectedError);
      return;
    }
    updateUserRfc(rfcRes[active_rfc_obj.rfc]);
    setIsLoading(false);
  };

  useEffect(() => {
    //Only update the RFC if we are not doing a subscription upgrade, since the plan page will manually update the REFC in the store so users see feedback immediately, but updating the RFC here would reset that state back to the original value
    if (active_rfc_obj && !!!urlHasSubscriptionUpgradeParameters) {
      updateRfc();
    }
  }, [activeRfc]);

  const rfcOptions = !!user.rfcs
    ? Object.keys(user.rfcs).map((rfc) => {
        return {
          key: rfc,
          value: rfc,
          label: (
            <Flex justify="space-between" gap={10}>
              <Space direction="vertical" size={0}>
                <span style={{ fontWeight: "bold" }}>{rfc}</span>
                {user.rfcs[rfc].sat_fiscal_name}
              </Space>
              <Flex justify="flex-end" gap={10}>
                {/* <Badge
                  count={4}
                  color="#a5a5a5ff"
                  style={{ marginTop: "12px" }}
                /> */}
                <StatusIcon
                  rfcObj={user.rfcs[rfc]}
                  showOk={false}
                  showTooltip={false}
                />
                {user.rfcs[rfc].plan_name !== "free" &&
                  user.stripe.subscription?.cancel_at && (
                    <ClockCircleOutlined style={{ color: "#C18622" }} />
                  )}
                {user.rfcs[rfc].plan_name === "free" &&
                  user.rfcs[rfc].has_eligible_trial && (
                    <GiftFilled style={{ color: "purple" }} />
                  )}
                {rfc === activeRfc && <CheckOutlined />}
              </Flex>
            </Flex>
          ),
          style: {
            backgroundColor: rfc === activeRfc ? "#f9f3f7" : null,
            width: smallCard ? "auto" : "350px",
            maxWidth: "350px",
          },
        };
      })
    : [];

  const handleActiveRfcChange = (activeRfc) => {
    if (activeRfc.key === "new-rfc") {
      setActiveRfc("nuevo-rfc");
      navigate("/certificados");
    } else {
      setActiveRfc(activeRfc.key);
    }
  };

  const menuProps = {
    items: rfcOptions.concat(
      { type: "divider" },
      {
        key: "new-rfc",
        label: (
          <Flex justify="space-between">
            <span style={{ fontWeight: "bold" }}>Nuevo RFC</span>
            <PlusOutlined />
          </Flex>
        ),
      },
    ),
    onClick: handleActiveRfcChange,
  };

  const handleNewRfcCancel = () => {
    setActiveRfc();
    navigate("/");
  };

  const refreshScheduleInfo = getRfcUpdateScheduleInfo(
    active_rfc_obj?.refresh_schedule,
  );

  const RfcSelectorDropdown = () => {
    return (
      <Dropdown menu={menuProps} trigger="click">
        <Button
          size="small"
          style={{
            borderColor: "#801794",
            color: "#801794",
            backgroundColor: "#F9F3F7",
          }}
        >
          <Space>
            {active_rfc_obj.rfc}
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
    );
  };

  const RefreshSchedule = () => {
    return active_rfc_obj.refresh_status === "inactive" ? (
      <Tooltip title="El RFC está inactivo y no se pueden recibir actualizaciones.">
        <Tag icon={<InfoCircleFilled />} className="cursor-help" color={"red"}>
          Inactivo
        </Tag>
      </Tooltip>
    ) : refreshScheduleInfo ? (
      <Tag
        className="cursor-pointer"
        color={refreshScheduleInfo === "Manual" ? "purple" : "green"}
        onClick={() => navigate("/alertas")}
      >
        {refreshScheduleInfo}
      </Tag>
    ) : (
      <Tooltip title="No hay información sobre el horario de actualización.">
        <Tag icon={<InfoCircleFilled />} className="cursor-help" color={"grey"}>
          N/A
        </Tag>
      </Tooltip>
    );
  };

  const CardLoadingSpinner = () => {
    return (
      isLoading && (
        <RedoOutlined
          spin={true}
          style={{
            color: "gray",
            marginLeft: smallCard ? "-10px" : "-10px",
          }}
        />
      )
    );
  };

  const FounderBadge = () =>
    active_rfc_obj.badges?.includes("beta_user") && (
      <span
        style={{
          display: "inline-flex",
          marginRight: smallCard ? "-5px" : null,
        }}
      >
        <FeatureTag
          type="founder_badge"
          cursor="help"
          tooltipTitle={
            <p>
              <b>
                <u>Miembro Fundador</u>
              </b>
              <br />
              ¡Eres miembro de factu.mx desde su lanzamiento!
            </p>
          }
        />
      </span>
    );

  let card = null;
  if (location.pathname === "/cuenta") {
    card = (
      <Flex style={{ marginTop: "-4px" }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/")}>
          Regresar
        </Button>
      </Flex>
    );
  } else if (location.pathname.startsWith("/admin")) {
    card = (
      <Flex style={{ marginTop: "-4px" }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => handleNewRfcCancel()}
        >
          Regresar
        </Button>
      </Flex>
    );
  } else if (activeRfc === "nuevo-rfc") {
    card = (
      <Flex style={{ marginTop: "-4px" }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleNewRfcCancel}>
          Cancelar y regresar
        </Button>
      </Flex>
    );
  } else if (active_rfc_obj) {
    if (smallCard) {
      card = (
        <Flex vertical gap={5}>
          <Flex align="center" gap={10}>
            <RfcSelectorDropdown />
            <PlanTag
              planName={active_rfc_obj.plan_name}
              cursor={"pointer"}
              isClickable
            />
            <CardLoadingSpinner />
          </Flex>
          <Flex align="center" gap={5}>
            <FounderBadge />
            <Text strong style={{ fontSize: "15px" }}>
              {active_rfc_obj.sat_fiscal_name}
            </Text>
          </Flex>
          <Flex gap={10} align="center">
            <Text>Horario de actualización:</Text>
            <RefreshSchedule />
          </Flex>
        </Flex>
      );
    } else {
      card = (
        <Flex align="center" gap={10}>
          <RfcSelectorDropdown />
          <Flex align="center" gap={0}>
            <FounderBadge />
            <Text strong style={{ fontSize: "15px" }}>
              {active_rfc_obj.sat_fiscal_name}
            </Text>
          </Flex>
          <PlanTag
            planName={active_rfc_obj.plan_name}
            cursor={"pointer"}
            isClickable
          />
          <Text>Horario de actualización:</Text>
          <RefreshSchedule />
          <CardLoadingSpinner />
        </Flex>
      );
    }
  }
  // else {
  //   //Add a fake button to keep alignment and spacing
  //   card = <Button style={{ visibility: "hidden" }}></Button>;
  // }

  return <div className={styleWrapperClass}>{card}</div>;
}
